/* View-in-Room AR — Single-file embed for Shopify
   - Finds any element with [data-view-in-room] and opens a modal with <model-viewer>
   - Supports AR on iOS (Quick Look), Android (Scene Viewer), and WebXR where available
   - Defaults to wall placement (good for art, mirrors, frames); set data-ar-placement="floor" if needed
   - No external CSS required; styles injected here
*/
(function () {
  const MV_SRC = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
  const MV_LEGACY_SRC = "https://unpkg.com/@google/model-viewer/dist/model-viewer-legacy.js";

  // --- Inject model-viewer once ---
  function ensureModelViewer() {
    return new Promise((resolve, reject) => {
      if (customElements.get("model-viewer")) return resolve();
      const script = document.createElement("script");
      script.type = "module";
      script.src = MV_SRC;
      script.onload = resolve;
      script.onerror = () => {
        // Fallback for older browsers
        const legacy = document.createElement("script");
        legacy.noModule = true;
        legacy.src = MV_LEGACY_SRC;
        legacy.onload = resolve;
        legacy.onerror = reject;
        document.head.appendChild(legacy);
      };
      document.head.appendChild(script);
    });
  }

  // --- Inject minimal styles for modal ---
  function injectStyles() {
    if (document.getElementById("vir-ar-styles")) return;
    const css = `
      .vir-ar-backdrop {
        position: fixed; inset: 0; background: rgba(0,0,0,.5);
        z-index: 99998; opacity: 0; pointer-events: none; transition: opacity .2s ease;
      }
      .vir-ar-backdrop.active { opacity: 1; pointer-events: auto; }
      .vir-ar-modal {
        position: fixed; inset: 0; display: grid; place-items: center; z-index: 99999;
        pointer-events: none;
      }
      .vir-ar-dialog {
        width: min(92vw, 980px); height: min(80vh, 720px);
        background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.25);
        transform: translateY(10px); opacity: 0; transition: transform .25s ease, opacity .25s ease;
        pointer-events: auto; display: grid; grid-template-rows: auto 1fr;
      }
      .vir-ar-modal.active .vir-ar-dialog { transform: translateY(0); opacity: 1; }
      .vir-ar-header {
        display:flex; align-items:center; justify-content:space-between; padding: 12px 16px; gap: 12px; border-bottom: 1px solid #eee;
      }
      .vir-ar-title { font: 600 16px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; }
      .vir-ar-close {
        appearance:none; border:0; background:#f5f5f5; border-radius: 999px; width: 36px; height: 36px;
        display:grid; place-items:center; cursor:pointer;
      }
      .vir-ar-close:hover { background:#ebebeb; }
      .vir-ar-body { position: relative; background:#fafafa; }
      .vir-ar-instructions {
        position: absolute; left: 12px; bottom: 12px; background: rgba(0,0,0,.65); color:#fff; padding:8px 10px; border-radius: 8px;
        font: 500 12px/1.3 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      }
      .vir-ar-footer {
        display:flex; justify-content: flex-end; gap: 8px; padding: 10px 12px; border-top: 1px solid #eee; background:#fff;
      }
      .vir-ar-action {
        appearance:none; border:0; background:#111; color:#fff; font-weight:600; padding:10px 14px; border-radius: 10px; cursor:pointer;
      }
      .vir-ar-action[disabled] { opacity:.6; cursor: not-allowed; }
      model-viewer.vir-ar-viewer {
        width: 100%; height: 100%; background: #fff;
      }
      /* Prevent body scroll when modal is open */
      .vir-ar-noscroll { overflow: hidden !important; }

      /* Visually hidden utility for slotted AR button so it doesn't duplicate UI */
      .vir-visually-hidden {
        position:absolute !important; width:1px; height:1px; padding:0; margin:-1px;
        overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0;
      }
    `;
    const style = document.createElement("style");
    style.id = "vir-ar-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  // --- Modal factory ---
  function createModal() {
    // Backdrop
    const backdrop = document.createElement("div");
    backdrop.className = "vir-ar-backdrop";

    // Modal
    const modal = document.createElement("div");
    modal.className = "vir-ar-modal";

    // Dialog
    const dialog = document.createElement("div");
    dialog.className = "vir-ar-dialog";
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", "View in your room");

    const header = document.createElement("div");
    header.className = "vir-ar-header";

    const title = document.createElement("div");
    title.className = "vir-ar-title";
    title.textContent = "View in your room";

    const close = document.createElement("button");
    close.className = "vir-ar-close";
    close.setAttribute("aria-label", "Close");
    close.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    const body = document.createElement("div");
    body.className = "vir-ar-body";

    const instructions = document.createElement("div");
    instructions.className = "vir-ar-instructions";
    instructions.textContent = "Tip: Move your phone to scan, then tap to place on the wall.";

    const footer = document.createElement("div");
    footer.className = "vir-ar-footer";

    const arBtn = document.createElement("button");
    arBtn.className = "vir-ar-action";
    arBtn.textContent = "Open AR";
    arBtn.disabled = true;

    header.append(title, close);
    body.append(instructions);
    footer.append(arBtn);
    dialog.append(header, body, footer);
    modal.append(dialog);

    function open() {
      document.body.append(backdrop, modal);
      requestAnimationFrame(() => {
        backdrop.classList.add("active");
        modal.classList.add("active");
        document.documentElement.classList.add("vir-ar-noscroll");
      });
    }

    function closeModal() {
      backdrop.classList.remove("active");
      modal.classList.remove("active");
      document.documentElement.classList.remove("vir-ar-noscroll");
      setTimeout(() => {
        backdrop.remove();
        modal.remove();
      }, 200);
    }

    backdrop.addEventListener("click", closeModal);
    close.addEventListener("click", closeModal);

    return { backdrop, modal, body, arBtn, title, open, close: closeModal };
  }

  // --- Create a <model-viewer> with the provided config ---
  // Returns { mv, slottedArBtn }
  function buildModelViewer({ glb, usdz, poster, arPlacement }) {
    const mv = document.createElement("model-viewer");
    mv.className = "vir-ar-viewer";
    if (glb) mv.setAttribute("src", glb);
    if (poster) mv.setAttribute("poster", poster);
    mv.setAttribute("camera-controls", "");
    mv.setAttribute("exposure", "1.0");
    mv.setAttribute("shadow-intensity", "0.0");
    mv.setAttribute("ar", "");
    mv.setAttribute("ar-modes", "webxr scene-viewer quick-look");
    mv.setAttribute("ar-scale", "fixed"); // For art/frames; set to "auto" to allow pinch-to-scale
    mv.setAttribute("ar-placement", arPlacement === "floor" ? "floor" : "wall");
    if (usdz) mv.setAttribute("ios-src", usdz);
    mv.setAttribute("reveal", "interaction");

    // Real, trusted AR activator for model-viewer (kept visually hidden)
    const slottedArBtn = document.createElement("button");
    slottedArBtn.slot = "ar-button";
    slottedArBtn.type = "button";
    slottedArBtn.className = "vir-visually-hidden";
    slottedArBtn.textContent = "Open AR";
    slottedArBtn.disabled = true;
    mv.appendChild(slottedArBtn);

    // Enable when the model is ready and AR is available
    const updateState = () => {
      // If mv.canActivateAR exists, use it; otherwise let MV handle availability.
      const can = ("canActivateAR" in mv) ? mv.canActivateAR : true;
      slottedArBtn.disabled = !can;
    };
    mv.addEventListener("load", updateState, { once: true });
    mv.addEventListener("ar-status", (e) => {
      if (e.detail?.status === "failed") slottedArBtn.disabled = true;
    });

    return { mv, slottedArBtn };
  }

  // --- Bind all triggers on the page ---
  function initTriggers() {
    const triggers = Array.from(document.querySelectorAll("[data-view-in-room]"));
    if (!triggers.length) return;

    triggers.forEach((btn) => {
      if (btn.__virBound) return;
      btn.__virBound = true;

      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const glb = btn.getAttribute("data-model-glb");
        const usdz = btn.getAttribute("data-model-usdz");
        const poster = btn.getAttribute("data-poster") || "";
        const productTitle = btn.getAttribute("data-title") || "View in your room";
        const arPlacement = btn.getAttribute("data-ar-placement") || "wall";

        if (!glb && !usdz) {
          console.warn("[View-in-Room AR] Missing data-model-glb and/or data-model-usdz.");
          alert("AR model not available for this product yet.");
          return;
        }

        try {
          injectStyles();
          await ensureModelViewer();
          const modal = createModal();
          modal.title.textContent = productTitle;

          const { mv, slottedArBtn } = buildModelViewer({ glb, usdz, poster, arPlacement });
          modal.body.prepend(mv);

          // Keep visible footer button; proxy-click the trusted slotted AR button
          modal.arBtn.disabled = true;

          const syncState = () => {
            modal.arBtn.disabled = !!slottedArBtn.disabled;
          };
          mv.addEventListener("load", syncState, { once: true });
          mv.addEventListener("ar-status", syncState);

          // Some browsers surface canActivateAR after load; poll briefly to sync state
          const canARInterval = setInterval(() => {
            if (!document.body.contains(mv)) return clearInterval(canARInterval);
            if ("canActivateAR" in mv) {
              slottedArBtn.disabled = !mv.canActivateAR;
              syncState();
              if (mv.canActivateAR) clearInterval(canARInterval);
            } else {
              clearInterval(canARInterval);
            }
          }, 300);

          modal.arBtn.addEventListener("click", () => slottedArBtn.click());

          modal.open();
        } catch (err) {
          console.error("[View-in-Room AR] Failed to initialize:", err);
          alert("Sorry, the AR viewer couldn’t be initialized on this device/browser.");
        }
      });
    });
  }

  // --- Public API for optional global init / overrides ---
  // Example: window.initViewInRoomAR({ selector: '.my-button' })
  window.initViewInRoomAR = function initViewInRoomAR(opts = {}) {
    const sel = opts.selector || "[data-view-in-room]";
    const placement = opts.defaultPlacement || "wall";
    // Update existing triggers to carry defaults if attributes are missing
    document.querySelectorAll(sel).forEach((el) => {
      if (!el.getAttribute("data-ar-placement")) el.setAttribute("data-ar-placement", placement);
    });
    initTriggers();
  };

  // Auto-init on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => window.initViewInRoomAR());
  } else {
    window.initViewInRoomAR();
  }
})();
